import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { Feature, FeatureCollection, Point, Polygon } from 'geojson'
import { MapRef } from 'react-map-gl'
import { DialogContent } from '@mui/material'
import { Box, Flex, Text } from '@fundamentals'
import {
  TimelineMap,
  formatAreaSize,
  PolygonEastingNorthingList,
  getPolygonVertices,
  rafThrottle,
} from '@modules/geolocation'
import { ModalDialogContainer } from '@common/modal'
import area from '@turf/area'
import { localizedStrings } from '@core/strings'

// The fill layer id GeoPolygon renders for a source/layer id of 'polygon'
// (see TimelineMap's <GeoPolygon id='polygon' />).
const POLYGON_FILL_LAYER_ID = 'polygon-fill-layer'
// How close (in screen pixels) the cursor must be to a corner to highlight it.
const VERTEX_HIT_RADIUS = 10

// Finds the polygon vertex (corner) nearest to a screen point, within
// `thresholdPx`. Vertex indices follow getPolygonVertices order — the same
// flattening PolygonEastingNorthingList uses — so the highlighted coordinate
// lines up with the list.
const findNearestVertex = (
  map: mapboxgl.Map,
  point: { x: number; y: number },
  features: FeatureCollection<Polygon>['features'],
  thresholdPx: number,
): { polygonIndex: number; vertexIndex: number } | null => {
  let best: { polygonIndex: number; vertexIndex: number } | null = null
  let bestDistSq = thresholdPx * thresholdPx

  for (const feature of features) {
    const polygonIndex = feature.properties?.polygonIndex
    if (typeof polygonIndex !== 'number') {
      continue
    }

    getPolygonVertices(feature).forEach((vertex, vertexIndex) => {
      const screen = map.project(vertex as [number, number])
      const dx = screen.x - point.x
      const dy = screen.y - point.y
      const distSq = dx * dx + dy * dy
      if (distSq <= bestDistSq) {
        bestDistSq = distSq
        best = { polygonIndex, vertexIndex }
      }
    })
  }

  return best
}

type SimpleMapModalProps = {
  projectId: number
  featurePoint?: Feature<Point>
  featurePolygon?: Feature<Polygon> | FeatureCollection<Polygon>
  userFeaturePoint?: Feature<Point>
  featurePointAccuracyRadius?: Feature<Polygon>
  footerActions?: ReactNode
  // Shows the polygon vertices as eastings/northings beneath the map.
  showEastingNorthingList?: boolean
}

export const SimpleMapModal: React.FC<SimpleMapModalProps> = ({
  projectId,
  featurePoint,
  featurePolygon,
  userFeaturePoint,
  featurePointAccuracyRadius,
  footerActions,
  showEastingNorthingList = false,
}) => {
  const mapRef = useRef<MapRef>()
  const [mapLoaded, setMapLoaded] = useState(false)
  const [hoveredPolygonIndex, setHoveredPolygonIndex] = useState<number | null>(
    null,
  )
  const [hoveredVertex, setHoveredVertex] = useState<{
    polygonIndex: number
    vertexIndex: number
  } | null>(null)

  // Normalise to a FeatureCollection whose features each carry a 1-based
  // `polygonIndex`, so map hovers can be matched back to the list rows (whose
  // indices come from feature array order).
  const normalisedFeaturePolygon = useMemo<
    FeatureCollection<Polygon> | undefined
  >(() => {
    if (!featurePolygon) return undefined

    const features =
      'features' in featurePolygon ? featurePolygon.features : [featurePolygon]

    return {
      type: 'FeatureCollection',
      features: features.map((feature, index) => ({
        ...feature,
        properties: {
          ...(feature.properties ?? {}),
          id: feature.properties?.id ?? index + 1,
          polygonIndex: index + 1,
        },
      })),
    }
  }, [featurePolygon])

  const calculatedArea = useMemo(() => {
    if (!normalisedFeaturePolygon) return null

    const squareMeters = Math.round(area(normalisedFeaturePolygon))

    return formatAreaSize(squareMeters)
  }, [normalisedFeaturePolygon])

  // Wire the polygon/vertex hover highlighting once the map is ready.
  useEffect(() => {
    if (!mapLoaded || !normalisedFeaturePolygon || !mapRef.current) return
    const map = mapRef.current.getMap()
    if (!map) return

    // Highlight the hovered polygon's row.
    const onPolygonMove = (
      e: mapboxgl.MapMouseEvent & {
        features?: mapboxgl.MapboxGeoJSONFeature[]
      },
    ) => {
      const polygonIndex = e.features?.[0]?.properties?.polygonIndex
      setHoveredPolygonIndex(
        typeof polygonIndex === 'number' ? polygonIndex : null,
      )
    }
    const clearPolygon = () => setHoveredPolygonIndex(null)

    map.on('mousemove', POLYGON_FILL_LAYER_ID, onPolygonMove)
    map.on('mouseleave', POLYGON_FILL_LAYER_ID, clearPolygon)

    // Highlight the matching coordinate while the cursor is near a corner. The
    // zones are fill/line layers (no rendered vertices to hit-test), so we find
    // the nearest vertex geometrically against the cursor's screen position.
    let lastVertex: { polygonIndex: number; vertexIndex: number } | null = null
    const reportVertex = (
      vertex: { polygonIndex: number; vertexIndex: number } | null,
    ) => {
      if (
        lastVertex?.polygonIndex === vertex?.polygonIndex &&
        lastVertex?.vertexIndex === vertex?.vertexIndex
      ) {
        return
      }
      lastVertex = vertex
      setHoveredVertex(vertex)
    }

    const onVertexMove = rafThrottle((e: mapboxgl.MapMouseEvent) => {
      reportVertex(
        findNearestVertex(
          map,
          e.point,
          normalisedFeaturePolygon.features,
          VERTEX_HIT_RADIUS,
        ),
      )
    })
    const clearVertex = () => reportVertex(null)

    map.on('mousemove', onVertexMove)
    map.on('mouseout', clearVertex)

    return () => {
      map.off('mousemove', POLYGON_FILL_LAYER_ID, onPolygonMove)
      map.off('mouseleave', POLYGON_FILL_LAYER_ID, clearPolygon)
      onVertexMove.cancel()
      map.off('mousemove', onVertexMove)
      map.off('mouseout', clearVertex)
    }
  }, [mapLoaded, normalisedFeaturePolygon])

  return (
    <ModalDialogContainer
      maxWidth={false}
      PaperProps={{ sx: { height: '95vh', maxHeight: '95vh' } }}
    >
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <Flex
          flexDirection='column'
          height='100%'
          width='100%'
          sx={{ minHeight: 0 }}
        >
          <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
            <TimelineMap
              mapRef={mapRef}
              onLoad={() => setMapLoaded(true)}
              projectId={projectId}
              featurePoint={featurePoint}
              featurePolygon={normalisedFeaturePolygon}
              userFeaturePoint={userFeaturePoint}
              featurePointAccuracyRadius={featurePointAccuracyRadius}
              style={{ height: '100%', width: '100%', borderRadius: 4 }}
            />
          </Box>

          {(!!footerActions || calculatedArea) && (
            <Flex alignItems='center' sx={{ pt: 2, flexShrink: 0 }}>
              {!!footerActions && (
                <Flex alignItems='center'>{footerActions}</Flex>
              )}
              {calculatedArea && (
                <Text color='text.secondary' fontSize={14} sx={{ ml: 'auto' }}>
                  {localizedStrings.getTotalDrawnAreaSize(
                    calculatedArea.displayValue,
                    calculatedArea.unitName,
                  )}
                </Text>
              )}
            </Flex>
          )}

          {showEastingNorthingList && normalisedFeaturePolygon && (
            <Box sx={{ flexShrink: 0, overflow: 'auto', maxHeight: '35%' }}>
              <PolygonEastingNorthingList
                featurePolygon={normalisedFeaturePolygon}
                verticesPerLine='all'
                highlightedIndex={hoveredPolygonIndex}
                highlightedVertex={hoveredVertex}
              />
            </Box>
          )}
        </Flex>
      </DialogContent>
    </ModalDialogContainer>
  )
}
